
/**
 *      Data-driven UI logic to display Backbone regions
 *
 *      @author Irina Patrikeeva <irina.i.ipatrikeeva@nasa.gov>
 *
 *      Created on 06/06/2017
 *      Modified on 06/11/2018
 */


/**
 * Create region element in the DOM inside the element with parentId which must exist
 * @param regionId {string} - element ID of the region
 * @param parentId {string} - element ID of the parent element
 */
function addRegionElement( regionId, parentId ) {
    var div = document.createElement('div'),
        regionEl;

    div.className = "full-row";
    addDOMElement( div, parentId );
    regionEl = document.createElement('div');
    regionEl.id = regionId;
    div.appendChild( regionEl );
}

/**
 * Create a new DOM element inside a parent element that already exists in the DOM
 * @param element {Node} - DOM element to attach
 * @param parentId {string} - parent element ID
 */
function addDOMElement( element, parentId ) {
    var parentElement = document.getElementById( parentId) ;
    if ( parentElement ) {
        parentElement.appendChild( element );
    }
}

/**
 * Gets region view layout
 * @param model {Object} - region view model
 * @param dataObject {Object} - parsed data to display
 * @param layoutView {Backbone.Marionette.LayoutView} - page layout view
 * @returns {*}
 */
function renderRegionLayout( model, dataObject, layoutView ) {

    var region,
        template,
        viewType,
        regionLayout,
        el;

    if (model && dataObject && layoutView) {

        region = model.name;
        template = model.template;
        viewType = model.viewType;

        if ( layoutView && layoutView.getRegion(region) ) {
            el = layoutView.getRegion(region).el || template;
            if (viewType === "CompositeView") {
                regionLayout = getCompositeRegionLayout(model, dataObject);
            } else {
                regionLayout = getSimpleRegionLayout(model, dataObject, el);
            }
            return regionLayout;
        }

    }
    return null;
}

/**
 * Get region view layout for each region
 * @param model {Object} - region view model
 * @param data {Object} - data to display
 * @param layoutView {Backbone.Marionette.LayoutView} - page layout view
 * @param jsonModel {object} - data JSON model
 * @returns {*}
 */
function getRegionLayout( model, data, layoutView, jsonModel ) {
    var regionLayout,
        dataObject;
    if ( model && data ) {
        dataObject = parseRecordData( model, data, jsonModel );
        if ( hasKeys( dataObject )) {
            regionLayout = renderRegionLayout( model, dataObject, layoutView );
        }
    }
    return regionLayout;
}

/**
 * Create a simple region layout
 * @param model {object} - region view JavaScript object model
 * @param dataObject {Object} - data object containing field labels, field labels and field types
 * @param el {string} - element id
 * @returns {*}
 */
function getSimpleRegionLayout(model, dataObject, el) {
    var regionLayout = null,
        layoutModel = new BackboneModel(),
        LayoutView,
        layoutCollection,
        key,
        viewType = model.viewType,
        nested = model.nested;

    if (viewType) {

        LayoutView = Backbone.Marionette[viewType].extend({
            initialize: function(e) {
                Backbone.Marionette.bindEntityEvents(this, this, this.events);
            },
            template: model.template,
            collection: BackboneCollection,
            triggers: model.triggers,
            events: model.events,
            hash: model.hash,
            performActionOne: function(e) {
                performAction(e, "performActionOne", this.hash);
            },
            performActionTwo: function(e) {
                performAction(e, "performActionTwo", this.hash);
            }
        });

        if (LayoutView) {
            // For nested templates, construct a Backbone.Collection with model for each field
            if (nested && nested === "true") {
                layoutCollection = new BackboneCollection();
                for (key in dataObject) {
                    if (dataObject.hasOwnProperty(key)) {
                        layoutModel = new BackboneModel();
                        layoutModel.set("field", key);
                        layoutModel.set("label", dataObject[key].label);
                        layoutModel.set("value", dataObject[key].value);
                        layoutModel.set("type", dataObject[key].type);
                        layoutCollection.add(layoutModel);
                    }
                }
                regionLayout = new LayoutView({ collection: layoutCollection, el: el });
            } else {
                // For non-nested templates, construct a Backbone.Model with attributes for each field
                for (key in dataObject) {
                    if (dataObject.hasOwnProperty(key)) {
                        layoutModel.set(key, dataObject[key].value);
                    }
                }
                regionLayout = new LayoutView({ model: layoutModel });
            }
        }
    }
    return regionLayout;
}

/**
 * Create a composite region layout
 * @param model {Object} - region view model JavaScript object
 * @param dataObject {Object} - data object containing field labels, field labels and field types
 * @returns {Backbone.Marionette.CompositeView}
 */
function getCompositeRegionLayout( model, dataObject ) {
    var regionLayout = null,
        layoutCollection,
        LayoutView,
        bbModel,
        nested,
        multiple,
        childObject,
        childView;

    if (model && model.viewType) {

        nested = model.nested;
        multiple = model.multiple;

        if ( model.default && multiple ) {
            childObject = model.childViewMultiple;
        } else {
            childObject = model.childView;
        }

        if ( childObject ) {
            childView = createBackboneChildView(childObject);
            LayoutView = Backbone.Marionette[model.viewType].extend({
                template: model.template,
                childView: childView,
                model: BackboneModel,
                childViewContainer: model.childViewContainer,
                events: model.events,
                hash: model.hash,
                performActionOne: function (e) {
                    performAction(e, "performActionOne", this.hash);
                },
                performActionTwo: function (e) {
                    performAction(e, "performActionTwo", this.hash);
                }
            });

            if (nested && nested === "true" && !multiple) {
                // For nested templates with one object, construct a Backbone.Collection with model for each field
                layoutCollection = getNestedLayoutCollection(dataObject);
            }
            else if (multiple && ( multiple === "true" || multiple === true ) && !nested) {
                // For a non-nested template with multiple objects, construct a Backbone.Collection with model for each object
                layoutCollection = getMultipleLayoutCollection(model, dataObject);
            }
            else if (nested && multiple && ( nested === "true" || nested === true ) && ( multiple === "true" || multiple === true )) {
                // For a nested template with multiple objects, construct a Backbone.Collection with model for each nested object
                layoutCollection = getNestedMultipleLayoutCollection( model, dataObject );
            }

            bbModel = createParentFieldsModel( model );
            regionLayout = new LayoutView( { collection: layoutCollection, model: bbModel } );
        }
    }

    return regionLayout;
}

/**
 * Get region Backbone view object by region name
 * @param regionName {string} - region name
 * @returns {*}
 */
function getRegionView(regionName) {
    var result = null,
        rm,
        region;
    if (GlobalPageState && GlobalPageState.layout) {
        rm = GlobalPageState.layout.regionManager;
        if (rm) {
            region = rm.get(regionName);
            if (region) {
                result = region.currentView;
            }
        }
    }
    return result;
}
/**
 * Get region Backbone view object by region id
 * @param regionId {string} - Backbone internal region ID
 * @returns {*}
 */
function getRegionViewById(regionId) {
    var rm,
        j,
        region,
        regionObject,
        view,
        result = null;
    if (regionId && GlobalPageState && GlobalPageState.layout) {
        rm = GlobalPageState.layout.regionManager;
        for (j = 0; j < GlobalPageState.length; j++) {
            region = GlobalPageState[j];
            if (region && rm) {
                regionObject = rm.get(region.name);
                if ( regionObject ) {
                    view = regionObject.currentView;
                    if (view && view.cid === regionId) {
                        result = view;
                    }
                }

            }
        }
    }
    return result;
}

/**
 * Get Backbone Marionette RegionManager object
 * @returns {*}
 */
function getRegionManager() {
    if (GlobalPageState && GlobalPageState.layout) {
        return GlobalPageState.layout.regionManager;
    }
}

/**
 *  Get region state variables from the global state
 * @param regionId {string} - Backbone internal region ID
 * @returns {*}
 */
function getRegionVars(regionId) {
    var rm,
        j,
        region,
        regionObject,
        view,
        result = null;

    if (GlobalPageState && GlobalPageState.layout) {
        rm = GlobalPageState.layout.regionManager;
        for (j = 0; j < GlobalPageState.length; j++) {
            region = GlobalPageState[j];
            if (region && rm) {
                regionObject = rm.get(region.name);
                if ( regionObject ) {
                    view = regionObject.currentView;
                    if (view && view.cid === regionId) {
                        result = region["variables"];
                    }
                }

            }
        }
    }

    return result;
}

/**
 * Get an object that represents a region view from JSON View Model
 * @param name {string} - region name
 * @returns {*}
 */
function getRegionObject( name ) {

    var collection,
        json,
        i,
        regions,
        result = null;

    if ( GlobalPageState && GlobalPageState.type ) {

        json = getJSONModel( "view", GlobalPageState.type, null );

        if (json) {
            collection = getBackboneCollection( json.properties );
            if (collection) {
                _.each(collection.models, function(model) {
                    if ( model.get("page") === GlobalPageState.currentPage ) {
                        regions = model.get("regions");
                        for (i = 0; i < regions.length; i++) {
                            if (name === regions[i].name) {
                                result = regions[i];
                            }
                        }
                    }
                });
            }
        }
    }
    return result;
}

/**
 * Get the name of the region
 * @param regionId {string} - region element ID in the DOM
 * @returns {*}
 */
function getRegionNameByDOMID(regionId) {
    var result = null,
        i,
        json,
        collection,
        regions,
        regionEl;

    if (GlobalPageState && GlobalPageState.type) {
        json = getJSONLayout( GlobalPageState.type, null );
        if (json) {
            collection = getBackboneCollection(json.properties);
            if (collection) {
                if (regionId.indexOf('#') === 0) {
                    regionEl = regionId;
                } else {
                    regionEl = '#' + regionId;
                }
                _.each(collection.models, function(model) {
                    if (model.get("page") === GlobalPageState.currentPage) {
                        regions = model.get("regions");
                        for (i = 0; i < regions.length; i++) {
                            if (regionEl === regions[i].el) {
                                result = regions[i].name;
                            }
                        }
                    }
                });
            }
        }
    }
    return result;
}

/**
 * Get the name of the region
 * @param regionId {string} - region Backbone ID
 * @returns {*}
 */
function getRegionNameById(regionId) {
    var rm,
        j,
        region,
        regionObject,
        view,
        result = null;
    if (regionId && GlobalPageState && GlobalPageState.layout) {
        rm = GlobalPageState.layout.regionManager;
        for (j = 0; j < GlobalPageState.length; j++) {
            region = GlobalPageState[j];
            if (region && rm) {
                regionObject = rm.get(region.name);
                view = regionObject.currentView;
                if (view && view.cid === regionId) {
                    result = region.name;
                }
            }
        }
    }
    return result;
}

/**
 * Get a region state object
 * @param pageName {string} - name of the page
 * @param type {string} - data type
 * @param regionName {string} - region name
 * @returns {*}
 */
function getRegionState( pageName, type, regionName ) {
    var pageRegions,
        i,
        result = null;
    if ( pageName && type ) {
        pageRegions = getPageState( pageName, type );
        if ( pageRegions ) {
            for ( i = 0; i < pageRegions.length; i++ ) {
                if ( pageRegions[i].name === regionName ) {
                    result = pageRegions[ i ];
                }
            }
        }
    }
    return result;
}

/**
 * Get JavaScript object that contains sorting field name and sorting order
 * @param regionName {string} - region name
 * @returns {object}
 */
function getRegionSortVars( regionName ) {
    var sortVars = {},
        regionState,
        variables,
        i;
    if ( GlobalPageState ) {
        regionState = getRegionState( GlobalPageState.currentPage, GlobalPageState.type, regionName );
        if ( regionState ) {
            variables = regionState.variables;
            if ( variables ) {
                for ( i = 0; i < variables.length; i++ ) {
                    if ( variables[i].value ) {
                        sortVars[ variables[ i ].name ] = variables[ i ].value;
                    }
                }
            }
        }
    }
    return sortVars;
}